import React from 'react';
import { ActionBadge, DimensionBars, ScoreRing } from './DecisionWidgets';

const ACTION_META = {
  buy: { label: '买入', hint: '是否适合新建仓' },
  add: { label: '加仓', hint: '是否适合提高仓位' },
  hold: { label: '持有', hint: '是否支持继续持有' },
  reduce: { label: '减仓', hint: '是否需要降低仓位' }
};

function fmtNumber(value, digits = 2, suffix = '') {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(digits)}${suffix}` : '—';
}

function OverviewKpi({ label, value, note, tone = 'neutral' }) {
  return (
    <div className={`overview-kpi tone-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note || '—'}</small>
    </div>
  );
}

function actionLabels(stateKey) {
  if (stateKey === 'support_confirmed') return { buy: '可列入候选', add: '小幅观察', hold: '支持持有', reduce: '未触发' };
  if (stateKey === 'caution_confirmed') return { buy: '暂缓', add: '暂停', hold: '谨慎持有', reduce: '进入复核' };
  return { buy: '等待确认', add: '暂缓', hold: '持有观察', reduce: '未形成多证据' };
}

function ActionImpactGrid({ analysis }) {
  const labels = actionLabels(analysis?.state?.key);
  return (
    <div className="overview-action-grid">
      {Object.entries(ACTION_META).map(([key, meta]) => {
        const tone = key === 'hold' ? 'support' : key === 'add' ? 'caution' : 'neutral';
        return (
          <div className={`overview-action-card tone-${tone}`} key={key}>
            <div><span>{meta.label}</span><small>{meta.hint}</small></div>
            <strong>{labels[key]}</strong>
            <p>{analysis?.actions?.[key] || '证据域尚未形成可用解释'}</p>
          </div>
        );
      })}
    </div>
  );
}

function EvidenceColumn({ title, tone, items, emptyText }) {
  return (
    <div className={`overview-evidence-column tone-${tone}`}>
      <h4>{title}</h4>
      {items?.length ? <ul>{items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}</ul> : <p>{emptyText}</p>}
    </div>
  );
}

function collectEvidence(result) {
  const items = result?.evidence_consistency?.items || [];
  return {
    support: items.filter((item) => item.direction === 'support').map((item) => `${item.label}：${item.fact}`).slice(0, 4),
    caution: items.filter((item) => item.direction === 'caution').map((item) => `${item.label}：${item.fact}`).slice(0, 4),
    mixed: items.filter((item) => ['mixed', 'unknown', 'neutral'].includes(item.direction)).map((item) => `${item.label}：${item.fact}`).slice(0, 5)
  };
}

function dailyStance(analysis) {
  if (analysis?.state?.key === 'support_confirmed') return { label: '可进入买入/小幅加仓复核', tone: 'positive', note: '多项独立证据偏正向，但仍按个人仓位分步执行' };
  if (analysis?.state?.key === 'caution_confirmed') return { label: '暂停买入与加仓，进入风险复核', tone: 'warn', note: '至少两项独立风险证据同向' };
  return { label: '持有观察，暂缓买入与加仓', tone: 'neutral', note: '正负证据矛盾或分化，等待下一净值与同类位置确认' };
}

function RiskSummary({ profile }) {
  const factors = profile?.factors || [];
  return (
    <div className="overview-risk-grid">
      {factors.map((factor) => (
        <div className={`overview-risk-item tone-${factor.tone || 'unknown'}`} key={factor.key}>
          <div><span>{factor.label}</span><small>可信度 {factor.confidence?.label || '—'}</small></div>
          <strong>{factor.level_label || '待确认'}</strong>
          <em>{factor.risk_score != null ? `${factor.risk_score}/100` : '—'}</em>
        </div>
      ))}
    </div>
  );
}

function DataHealth({ result }) {
  const profile = result?.product_profile || {};
  const disclosure = result?.product_disclosure || result?.document_insight || {};
  const riskQuality = result?.risk_exposure_profile?.data_quality || {};
  const gaps = disclosure.data_gaps || [];
  const benchmark = result?.benchmark_analysis || {};
  const coverage = result?.evidence_consistency?.coverage;
  return (
    <div className="overview-health-grid">
      <div><span>最新净值</span><strong>{profile.latest_nav_date || '缺失'}</strong><small>产品动态数据</small></div>
      <div><span>最新报告</span><strong>{riskQuality.latest_report_period || '缺失'}</strong><small>{riskQuality.report_age_days != null ? `距报告期末 ${riskQuality.report_age_days} 天` : '未计算时效'}</small></div>
      <div><span>披露状态</span><strong>{disclosure.status_label || '待接入'}</strong><small>仅使用审核发布数据</small></div>
      <div><span>基准解析</span><strong>{benchmark.parseQuality || '缺失'}</strong><small>行情覆盖 {benchmark.market_index_sources?.db || 0} 个入库成分</small></div>
      <div><span>证据域覆盖</span><strong>{coverage ? `${coverage.available}/${coverage.total}` : '—'}</strong><small>不等于字段或持仓完整度</small></div>
      <div className={gaps.length ? 'has-gap' : ''}><span>明确缺口</span><strong>{gaps.length}</strong><small>{gaps[0] || '暂无明确缺口'}</small></div>
    </div>
  );
}

export default function ProductOverviewTab({ result }) {
  if (!result) return null;
  const profile = result.product_profile || {};
  const risk = result.risk_exposure_profile;
  const evidence = collectEvidence(result);
  const consistency = result.evidence_consistency;
  const stance = dailyStance(consistency);
  const drawdown = result.drawdown_path_analysis;
  const trend = result.performance_trend;
  const transmission = result.market_pressure_transmission;
  const exposure = transmission?.exposures || {};

  return (
    <div className="product-overview-tab">
      <section className="overview-hero">
        <div className="overview-hero-score"><ScoreRing score={result.score} /><span>现有规则辅助分</span></div>
        <div className="overview-hero-main">
          <div className="overview-eyebrow">每日决策结论 · {consistency?.state?.label || '证据待补充'}</div>
          <h2>{profile.product_full_name || result.product_name}</h2>
          <div className="overview-product-meta">
            <span>{profile.sale_code || result.product_id}</span>
            <span>{profile.manager_org || result.agent_org}</span>
            <span>{profile.product_type || result.product_type || '—'}</span>
            <span>{profile.risk_level || '—'}</span>
            <span>{profile.term_text || '—'}</span>
          </div>
          <div className={`overview-daily-stance tone-${stance.tone}`}>
            <strong>{stance.label}</strong><p>{stance.note}</p>
          </div>
          <div className="overview-current-action">
            <span>现有规则标签</span><ActionBadge action={result.action} label={result.label} />
            <p>{result.summary || '当前暂无摘要'}</p>
          </div>
          <p className="overview-hero-note">首页结论优先采用六域证据一致性；现有分数和动作标签保留作对照，新分析暂不修改评分。</p>
        </div>
        <div className="overview-dimensions">
          <h3>四维状态</h3>
          <DimensionBars dimensions={result.dimensions} />
        </div>
      </section>

      <section className="overview-kpi-grid">
        <OverviewKpi label="近期表现" value={trend?.state_label || '—'} note={`${trend?.basis_label || '窗口不足'} · 可信度${trend?.confidence_label || '—'}`} tone={trend?.state === 'weakening' ? 'warn' : trend?.state === 'improving' ? 'positive' : 'neutral'} />
        <OverviewKpi label="当前回撤路径" value={drawdown?.state?.label || '—'} note={drawdown?.current_episode ? `当前回撤 ${fmtNumber(drawdown.current_episode.current_drawdown_bp, 1, 'bp')} · 修复 ${fmtNumber(drawdown.current_episode.repair_pct, 1, '%')}` : `全部${drawdown?.source_points || 0}个净值点`} tone={['expanding', 'near_trough', 'underwater'].includes(drawdown?.state?.key) ? 'warn' : 'positive'} />
        <OverviewKpi label="严格同类位置" value={result.peer_percentile != null ? fmtNumber(result.peer_percentile, 0, '%') : '—'} note={`${result.peer_metric || '同类指标'} · ${result.strict_peer_comparison?.selected_label || '边界待定'}`} tone={Number(result.peer_percentile) >= 60 ? 'positive' : Number(result.peer_percentile) <= 35 ? 'warn' : 'neutral'} />
        <OverviewKpi label="市场压力传导" value={transmission?.overall?.label || '—'} note={transmission?.actions?.hold?.text || '市场数据不足'} tone={transmission?.overall?.tone === 'mixed_positive' ? 'positive' : 'neutral'} />
        <OverviewKpi label="关键披露暴露" value={`债 ${fmtNumber(exposure.bonds_pct, 2, '%')} · 权 ${fmtNumber(exposure.equity_pct, 2, '%')}`} note={`现金 ${fmtNumber(exposure.cash_pct, 2, '%')} · 杠杆 ${fmtNumber(exposure.leverage_pct, 2, '%')}`} tone={Number(exposure.leverage_pct) > 120 ? 'warn' : 'neutral'} />
        <OverviewKpi label="证据与数据" value={consistency?.coverage ? `${consistency.coverage.available}/${consistency.coverage.total}域可用` : '—'} note={`明确字段缺口 ${(result.product_disclosure?.data_gaps || []).length} 项`} tone={(result.product_disclosure?.data_gaps || []).length >= 3 ? 'warn' : 'neutral'} />
      </section>

      <section className="overview-section">
        <div className="overview-section-head"><div><span>今日操作框架</span><h3>买入、加仓、持有、减仓怎么看</h3></div><small>来自六域证据一致性，不进入评分</small></div>
        <ActionImpactGrid analysis={consistency} />
      </section>

      <section className="overview-section">
        <div className="overview-section-head"><div><span>结论依据</span><h3>支持、谨慎与分化证据</h3></div><small>每条均可在分项 Tab 追溯</small></div>
        <div className="overview-evidence-grid">
          <EvidenceColumn title="主要支持因素" tone="support" items={evidence.support} emptyText="暂无明确支持因素" />
          <EvidenceColumn title="需要谨慎" tone="caution" items={evidence.caution} emptyText="暂无明确谨慎因素" />
          <EvidenceColumn title="分化与待确认" tone="trigger" items={evidence.mixed} emptyText="暂无分化或缺失证据" />
        </div>
      </section>

      <section className="overview-section">
        <div className="overview-section-head"><div><span>风险雷达</span><h3>六类风险摘要</h3></div><small>详细证据见“风险与决策”</small></div>
        <RiskSummary profile={risk} />
      </section>

      <section className="overview-section">
        <div className="overview-section-head"><div><span>数据健康度</span><h3>这份结论建立在什么数据上</h3></div><small>缺失和过期数据会降低可信度</small></div>
        <DataHealth result={result} />
      </section>
    </div>
  );
}
